import { useCallback, useState } from "react";

import { b24Reconnect } from "helpers/Sources/b24/b24Source";
import { bigqueryReconnect } from "helpers/Sources/bigquery/bigquerySource";
import { ftpReconnect } from "helpers/Sources/createSource";
import { googleSendCode } from "helpers/Sources/googleSheets";
import { pipedriveReconnect } from "helpers/Sources/pipedrive/pipedriveSource";
import { zendeskReconnect } from "helpers/Sources/zendesk/zendeskSource";
import { zohoReconnect } from "helpers/trackers/createTracker";

import SwalAlertComp from "components/SwalAlert";

import { Language, Source } from "../SourceModal.types";

interface UseSourceReconnectOptions {
    source: Source;
    language?: Language;
    onReconnectSuccess?: () => void;
}

interface UseSourceReconnectReturn {
    isLoading: boolean;
    setIsLoading: (loading: boolean) => void;
    reconnectWithData: (data: any) => Promise<void>;
    reconnectFtp: (data: any) => Promise<void>;
    reconnectZendesk: (data: any) => Promise<void>;
    reconnectPipedrive: (data: any) => Promise<void>;
    reconnectB24: (data: any) => Promise<void>;
    reconnectBigquery: (data: any) => Promise<void>;
    reconnectGoogleSheets: (data: any) => Promise<void>;
}

export const useSourceReconnect = ({
    source,
    language,
    onReconnectSuccess,
}: UseSourceReconnectOptions): UseSourceReconnectReturn => {
    const [isLoading, setIsLoading] = useState(false);

    const handleSuccess = useCallback(
        (response: any) => {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            onReconnectSuccess?.();
        },
        [language, onReconnectSuccess],
    );

    const handleError = useCallback(
        (response: any) => {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    response?.message ??
                    "An error occurred during reconnection",
                icon: "error",
            });
        },
        [language],
    );

    const reconnectWithData = useCallback(
        async (data: any) => {
            setIsLoading(true);
            try {
                const response = await zohoReconnect(source.id, data);
                if (response.status) {
                    handleSuccess(response);
                } else {
                    handleError(response);
                }
            } catch {
                handleError({});
            } finally {
                setIsLoading(false);
            }
        },
        [source.id, handleSuccess, handleError],
    );

    const reconnectFtp = useCallback(
        async (data: any) => {
            setIsLoading(true);
            try {
                const response = await ftpReconnect(source.id, data);
                if (response.status) {
                    handleSuccess(response);
                } else {
                    handleError(response);
                }
            } catch {
                handleError({});
            } finally {
                setIsLoading(false);
            }
        },
        [source.id, handleSuccess, handleError],
    );

    const reconnectZendesk = useCallback(
        async (data: any) => {
            setIsLoading(true);
            try {
                const response = await zendeskReconnect(source.id, data);
                if (response.status) {
                    handleSuccess(response);
                } else {
                    handleError(response);
                }
            } catch {
                handleError({});
            } finally {
                setIsLoading(false);
            }
        },
        [source.id, handleSuccess, handleError],
    );

    const reconnectPipedrive = useCallback(
        async (data: any) => {
            setIsLoading(true);
            try {
                const response = await pipedriveReconnect(source.id, data);
                if (response.status) {
                    handleSuccess(response);
                } else {
                    handleError(response);
                }
            } catch {
                handleError({});
            } finally {
                setIsLoading(false);
            }
        },
        [source.id, handleSuccess, handleError],
    );

    const reconnectB24 = useCallback(
        async (data: any) => {
            setIsLoading(true);
            try {
                const response = await b24Reconnect(source.id, data);
                if (response.status) {
                    handleSuccess(response);
                } else {
                    handleError(response);
                }
            } catch {
                handleError({});
            } finally {
                setIsLoading(false);
            }
        },
        [source.id, handleSuccess, handleError],
    );

    const reconnectBigquery = useCallback(
        async (data: any) => {
            setIsLoading(true);
            try {
                const response = await bigqueryReconnect(source.id, data);
                if (response.status) {
                    handleSuccess(response);
                } else {
                    handleError(response);
                }
            } catch {
                handleError({});
            } finally {
                setIsLoading(false);
            }
        },
        [source.id, handleSuccess, handleError],
    );

    const reconnectGoogleSheets = useCallback(
        async (data: any) => {
            setIsLoading(true);
            try {
                const response = await googleSendCode(data);
                if (response.status) {
                    handleSuccess(response);
                } else {
                    handleError(response);
                }
            } catch {
                handleError({});
            } finally {
                setIsLoading(false);
            }
        },
        [handleSuccess, handleError],
    );

    return {
        isLoading,
        setIsLoading,
        reconnectWithData,
        reconnectFtp,
        reconnectZendesk,
        reconnectPipedrive,
        reconnectB24,
        reconnectBigquery,
        reconnectGoogleSheets,
    };
};

export default useSourceReconnect;
